import React from "react";
import PropTypes from "prop-types";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import {
  ButtonHOC,
  DefaultBtn,
  DefaultMSFNavBtn,
  DefaultSearchBtn,
} from "../HOCs";
import {
  commonStylesBasic,
  commonStylesIconBasic,
  commonStylesPlaceholderBasic,
  getCommonStylesLabelBasic,
} from "../helpers";
import jssVariables from "../../../../jss_setup";

const commonStyles = {
  ...commonStylesBasic,
  minWidth: "10rem",
  borderRadius: 0,
  padding: `calc(${jssVariables.normal_gap} / 3) calc(${jssVariables.normal_gap} * 1)`,
  background: jssVariables.colors.base_color,
  border: `3px solid ${jssVariables.colors.base_color}`,
  "&:hover": {
    color: jssVariables.colors.base_color,
    background: "white",
  },
};

const icon = {
  ...commonStylesIconBasic,
};

const ButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderBasic,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  ButtonDefault: (props) => ({
    ...commonStyles,
    minWidth: "max-content",
    minHeight: "max-content",
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: jssVariables.colors.base_color,
    color: "white",
    border: `3px solid ${jssVariables.colors.base_color}`,
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  ButtonReverted: (props) => ({
    ...commonStyles,
    minWidth: "max-content",
    minHeight: "max-content",
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "white",
    color: jssVariables.colors.base_color,
    border: `3px solid ${jssVariables.colors.base_color}`,
    "&:hover": {
      color: "white",
      background: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "ButtonReverted", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ButtonDefault: (props) => ({
      ..._.get(props.customStyles, "ButtonDefault.smallscreen", {}),
    }),
    ButtonReverted: (props) => ({
      ..._.get(props.customStyles, "ButtonReverted.smallscreen", {}),
    }),
  },
});
var Button = ({ text, showIcon, ...props }) => {
  return (
    <DefaultBtn {...props}>
      <span className={props.classes.label}>{text}</span>
      {showIcon && (
        <InlineIcon icon="fa:angle-right" className={props.classes.icon} />
      )}
    </DefaultBtn>
  );
};
Button.defaultProps = {
  text: "",
  showIcon: true,
  classes: {},
};
Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showIcon: PropTypes.bool,
};
Button = ButtonHOC(Button, ButtonStyles);

//---------------------------------------
//-------- SearchButton -----------------
const SearchButtonStyles = createUseStyles({
  placeholder: (props) => ({
    height: "3rem !important",
    minWidth: "10rem",
    borderRadius: jssVariables.half_gap,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  SearchButton: (props) => ({
    ...commonStyles,
    background: "white",
    border: "3px solid white",
    color: jssVariables.colors.base_color,
    height: "3rem",
    fontSize: "medium",
    "&:hover": {
      background: jssVariables.colors.base_color,
      color: "white",
    },
    ..._.get(props.customStyles, "SearchButton", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    fontSize: "x-large",
    marginTop: ".2rem",
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
});
var SearchButton = ({ loading, text, ...props }) => {
  return (
    <DefaultSearchBtn {...props}>
      {loading ? (
        <InlineIcon
          icon="line-md:loading-twotone-loop"
          className={props.classes.icon}
        />
      ) : (
        <span className={props.classes.label}>{text}</span>
      )}
    </DefaultSearchBtn>
  );
};
SearchButton.defaultProps = {
  loading: false,
  text: "",
};
SearchButton.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
};
SearchButton = ButtonHOC(SearchButton, SearchButtonStyles);

const MSFNavBtnStyles = createUseStyles({
  placeholder: (props) => ({
    height: "2.5rem!important",
    minWidth: "4rem!important",
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  button: (props) => ({
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "none",
    borderBottom: `2px solid transparent`,
    fontWeight: "normal",
    height: "max-content",
    "&:hover:enabled": {
      borderBottom: `2px solid ${jssVariables.colors.text.dark}`,
      fontWeight: "bold",
    },
    "&:disabled": {
      borderBottom: `2px solid ${jssVariables.colors.text.dark}`,
      fontWeight: "bold",
      cursor: "unset",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    color: jssVariables.colors.text.dark,
    fontSize: "large",
    fontWeight: "bold",

    textTransform: "uppercase",
    ..._.get(props.customStyles, "label", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    button: (props) => ({
      ..._.get(props.customStyles, "button.smallscreen", {}),
    }),
    label: (props) => ({
      ...getCommonStylesLabelBasic(props),
      fontSize: "x-large",
      fontWeight: "bold",

      ..._.get(props.customStyles, "label.smallscreen", {}),
    }),
  },
});
var MSFNavBtn = ({ text, showIcon, ...props }) => {
  return (
    <DefaultMSFNavBtn {...props}>
      <span className={props.classes.label}>{text}</span>
    </DefaultMSFNavBtn>
  );
};
MSFNavBtn.defaultProps = {
  text: "",
  classes: {},
};
MSFNavBtn.propTypes = {
  text: PropTypes.string.isRequired,
};
MSFNavBtn = ButtonHOC(MSFNavBtn, MSFNavBtnStyles);

export { Button, SearchButton, MSFNavBtn };
